import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import { z } from "zod";

import {
  getObjectDetail,
  getToolInputString,
  parseRecordOrNull,
  quotedText,
} from "../shared";
import { getToolPartOutputRecord, naturalDetail } from "./bedrock-shared";

const nonEmptyStringSchema = z.string().trim().min(1);

function formatScheduledTime({ value }: { value: string }) {
  const scheduledAt = new Date(value);
  if (Number.isNaN(scheduledAt.getTime())) {
    return value;
  }

  const offsetMatch = value.match(/([+-])(\d{2}):(\d{2})$/);
  const offsetMinutes = offsetMatch
    ? (offsetMatch[1] === "-" ? -1 : 1) *
      (Number(offsetMatch[2]) * 60 + Number(offsetMatch[3]))
    : null;
  const displayDate =
    offsetMinutes === null
      ? scheduledAt
      : new Date(scheduledAt.getTime() + offsetMinutes * 60_000);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(displayDate);

  if (offsetMinutes === -420 || offsetMinutes === -480) {
    return `${time} Pacific`;
  }

  return offsetMinutes === 0 ? `${time} UTC` : time;
}

export function getTaskInputDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const title = getToolInputString({ properties: ["title"], toolPart });
  const emoji =
    getToolInputString({ properties: ["emoji"], toolPart }) ??
    getObjectDetail({
      properties: ["emoji"],
      value: parseRecordOrNull(outputRecord?.task),
    });
  const scheduledAt = getToolInputString({
    properties: ["scheduledAt"],
    toolPart,
  });

  return naturalDetail({
    parts: [
      {
        label: "named",
        value: title ? `${emoji ? `${emoji} ` : ""}${title}` : null,
      },
      {
        label: "for",
        value: scheduledAt ? formatScheduledTime({ value: scheduledAt }) : null,
      },
    ],
  });
}

function getTaskListItemDetail({ task }: { task: unknown }) {
  const taskRecord = parseRecordOrNull(task);
  const title = getObjectDetail({ properties: ["title"], value: taskRecord });
  const emoji = getObjectDetail({ properties: ["emoji"], value: taskRecord });
  if (!title && !emoji) {
    return null;
  }

  return quotedText({
    value: `${emoji ? `${emoji} ` : ""}${title ?? "Untitled task"}`,
  });
}

function getOutputTasks({ toolPart }: { toolPart: OpencodeToolPart }) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  return [
    ...(Array.isArray(outputRecord?.tasks) ? outputRecord.tasks : []),
    ...(outputRecord?.task ? [outputRecord.task] : []),
  ];
}

function getInputTask({ toolPart }: { toolPart: OpencodeToolPart }) {
  const taskId = getToolInputString({ properties: ["taskId", "id"], toolPart });
  const title = getToolInputString({ properties: ["title"], toolPart });
  const emoji = getToolInputString({ properties: ["emoji"], toolPart });
  return taskId || title || emoji ? [{ taskId, title, emoji }] : [];
}

export function getDeletedTaskDetail({
  toolPart,
  toolParts,
}: {
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}) {
  const deletedTaskId = getToolInputString({
    properties: ["taskId"],
    toolPart,
  });
  if (!deletedTaskId) {
    return null;
  }

  for (const sourceToolPart of [...toolParts].reverse()) {
    const matchingTask = [
      ...getOutputTasks({ toolPart: sourceToolPart }),
      ...getInputTask({ toolPart: sourceToolPart }),
    ].find((task) => {
      const taskRecord = parseRecordOrNull(task);
      return [taskRecord?.id, taskRecord?.taskId].some((taskId) => {
        const parsedTaskId = nonEmptyStringSchema.safeParse(taskId);
        return parsedTaskId.success && parsedTaskId.data === deletedTaskId;
      });
    });
    const detail = getTaskListItemDetail({ task: matchingTask });
    if (detail) {
      return `named ${detail}`;
    }
  }

  return `with ID ${quotedText({ value: deletedTaskId })}`;
}

export function getTaskListDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const tasks = Array.isArray(outputRecord?.tasks) ? outputRecord.tasks : null;
  if (!tasks) {
    return null;
  }

  if (tasks.length === 0) {
    return "and found none";
  }

  return `including ${tasks.length === 1 ? "1 task" : `${tasks.length} tasks`}`;
}
